export default class Car {
    constructor(parent) {
        this.parent = parent;        
        this.hashManager = parent.hashManager;
    }

    fromData(carData) {
        this.grade = carData.gradeCode;
        this.engine = carData.engineTDCode;
        this.colour = carData.defaultColourTrim.colour;
        this.trim = carData.defaultColourTrim.trim;
        this.accessories = [carData.defaultWheel];
        this.additionalProducts = [];
        this.mandatoryEuroOption = null;
    }

    fromUrl() {
        this.grade = this.hashManager.get('grade');
        this.engine = this.hashManager.get('engine');
        this.colour = this.hashManager.get('colour');
        this.trim = this.hashManager.get('trim');
        this.accessories = this.hashManager.get('accessories')?.split(',') || [];
        this.additionalProducts = this.hashManager
            .get('additionalProducts')
            ?.split(',')
            ?.filter((p) => p) ?? [];
        this.mandatoryEuroOption = this.hashManager.get('mandatoryEuroOption');
    }

    fromMTOC(mtoc) {
        const { mto, colour, trim} = this.parse(mtoc);
        const carData = this.parent.data.cars.find((car) => car?.mto === mto);
        const colourTrim = carData?.colourTrims?.find((ct) => ct.colourMTOCE === colour && ct.trimMTOCE === trim);

        this.grade = carData?.gradeCode;
        this.engine = carData?.engineTDCode;
        this.colour = colourTrim?.colour;
        this.trim = colourTrim?.trim;
        this.accessories = [carData?.defaultWheel].filter((a) => a);
        this.additionalProducts = this.hashManager
            .get('additionalProducts')
            ?.split(',')
            ?.filter((p) => p) ?? [];
        this.mandatoryEuroOption = null;
    }

    fromMTOCE() {
        const mtoce = this.hashManager.get('mtoce');
        const mandatoryEuroOption = this.hashManager.get('mandatoryEuroOption');
        const { mtoc, euroOptions} = this.parse(mtoce);
        this.fromMTOC(mtoc);
        const accessories = euroOptions
            ?.filter((euroOption) => !mandatoryEuroOption || euroOption !== mandatoryEuroOption)
            ?.map((euroOption) => this.findAccessoryFromEuroOption(euroOption)) ?? [];
        const wheels = this.parent.data.wheels.map((wheel) => wheel.partNumber);
        const wheel = accessories?.find((accessory) => wheels.includes(accessory));
        if(!wheel) {
            const defaultWheel = this.data()?.defaultWheel;
            accessories.push(defaultWheel);
        }
        this.accessories = accessories;
        this.additionalProducts = this.hashManager
            .get('additionalProducts')
            ?.split(',')
            ?.filter((p) => p) ?? [];
        this.mandatoryEuroOption = mandatoryEuroOption || null;
    }

    findAccessoryFromEuroOption(euroOption) {
        const accessories = Object.values(this.parent.data.accessories);
        const accessory = accessories.find((acc) => acc.mtoceGradeMap?.[this.grade] === euroOption && (!acc.extColour || acc.extColour === this.colour)) ??
            accessories.find((acc) => acc.mtoce === euroOption && (!acc.extColour || acc.extColour === this.colour))
        return accessory?.partNumber;
    }

    parse(mtoce) {
        const split = mtoce?.split('|');
        const mto = split[0];        
        const colour = split[1];
        const trim = split[2];
        const mtoc = `${mto}|${colour}|${trim}`;
        const euroOptions = split[3]?.split('~')?.filter((e) => e);
        return {
            mto,
            colour,
            trim,
            mtoc,
            euroOptions,
        }
    }

    toUrl() {
        this.hashManager.set('mtoce', this.mtoce());
        this.hashManager.set('grade', this.grade);
        this.hashManager.set('engine', this.engine);
        this.hashManager.set('colour', this.colour);
        this.hashManager.set('trim', this.trim);
        this.hashManager.set('accessories', this.accessories.join(','));
        this.hashManager.set('additionalProducts', this.additionalProducts.join(','));
        this.hashManager.set('mandatoryEuroOption', this.mandatoryEuroOption);
        this.hashManager.update();
    }

    update(testCar) {
        this.grade = testCar.grade;
        this.engine = testCar.engine;
        this.colour = testCar.colour;
        this.trim = testCar.trim;
        this.accessories = [...testCar.accessories];
        this.additionalProducts = [...testCar.additionalProducts];
        this.mandatoryEuroOption = testCar.mandatoryEuroOption;
        this.toUrl();
        this.parent.update();
    }

    clone() {
        const car = new Car(this.parent);
        car.grade = this.grade;
        car.engine = this.engine;
        car.colour = this.colour;
        car.trim = this.trim;
        car.accessories = [...this.accessories];
        car.additionalProducts = [...this.additionalProducts];
        car.mandatoryEuroOption = this.mandatoryEuroOption;
        return car;
    }

    // Car map created by DataHelper on page load
    data() {
        return this.parent.data.carMap[`${this.grade}-${this.engine}`];
    }

    gradeData() {
        return this.parent.data.gradeMap[this.grade];
    }

    wheel() {
        const wheels = this.parent.data.wheels.map((w) => w.partNumber);
        return this.accessories.find((a) => wheels.includes(a));
    }

    wheelData() {
        const wheel = this.wheel();
        return this.parent.data.accessories[wheel];
    }

    accessoriesData() {
        return this.accessories
            .map((a) => this.parent.data.accessories[a])
            .filter((a) => a);
    }

    accessoriesWithoutWheelData() {
        return this.accessories
            .filter((a) => a !== this.wheel())
            .map((a) => this.parent.data.accessories[a])
            .filter((a) => a);
    }

    additionalProductsData() {
        return this.additionalProducts
            .map((ap) => this.parent.data.additionalProductsMap[ap])
            .filter((a) => a);
    }

    // ColourTrim map created by DataHelper on page load
    colourTrim() {
        return this.data().colourTrimMap[`${this.colour}-${this.trim}`];
    }

    isDefaultWheel() {
        return this.data().defaultWheel === this.wheel();
    }

    isDefaultColour() {
        return (
            this.colourTrim().colour === this.data().defaultColourTrim.colour
        );
    }

    isDefaultTrim() {
        return this.colourTrim().trim === this.data().defaultColourTrim.trim;
    }

    m() {
        return this.mto().split('~')?.[0];
    }

    mto() {
        const { mto } = this.data();
        return mto;
    }

    mtoc() {
        const mto = this.mto();
        const c1 = this.colourTrim().colourMTOCE;
        const c2 = this.colourTrim().trimMTOCE;
        return `${mto}|${c1}|${c2}`;
    }

    e() {
        return [...new Set([
            ...([this.mandatoryEuroOption].filter((eo) => eo)),
            ...(this.accessories.map((a) => this.accessoryMTOCE(a)).filter((a) => a))
        ])].sort().join('~');
    }

    mtoce() {
        const mtoc = this.mtoc();
        const e = this.e();
        return `${mtoc}|${e}`;
    }

    mtoe() {
        const mto = this.mto();
        const e = this.e();
        return `${mto}|${e}`;
    }

    additionalMTOCE() {
        const m = this.m();
        return this.additionalProducts.map((ap) => `${m}|${ap}`);
    }

    wheelMTOCE() {
        return this.accessoryMTOCE(this.wheel());
    }

    accessoryMTOCE(accessory) {
        const data = this.parent.data.accessories[accessory];
        if(data.mtoceGradeMap) {
            return data.mtoceGradeMap[this.grade] 
                ?? Object.values(data.mtoceGradeMap)?.[0] 
                ?? data.mtoce;
        }
        return data.mtoce;
    }
}
